import { useState, useRef, useEffect } from "react";
// Import types only to avoid SSR issues
/* import type { FFmpeg } from "@ffmpeg/ffmpeg"; */
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from '@ffmpeg/util';

export function VideoEditorDashboard() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFFmpegLoaded, setIsFFmpegLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const ffmpegRef = useRef(new FFmpeg());

  // Load FFmpeg on component mount, but only in the browser
  useEffect(() => {
    console.log("Browser environment detected, attempting to load FFmpeg");

    const loadFFmpeg = async () => {
      try {
        console.log("Starting dynamic import of FFmpeg modules");


        console.log("Creating FFmpeg instance");
        // Create a new FFmpeg instance if it doesn't exist
        if (!ffmpegRef.current.loaded) {
            await ffmpegRef.current.load();
        }

          setIsFFmpegLoaded(true);
      } catch (err) {
          console.error("Error loading FFmpeg:", err);
        const error = err as Error;
        setError(`Failed to load video processing library: ${error.message || 'Unknown error'}`);
      }
    };

    loadFFmpeg();
  }, []);

  useEffect(() => {
    // Cleanup function
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);

    // Reset previous state when a new file is selected
    setOutputUrl(null);
    setError(null);
    setProgress(0);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const processVideo = async () => {
    if (!selectedFile || !isFFmpegLoaded || !ffmpegRef.current) return;

    try {
      // Dynamically import fetchFile only when needed
      const ffmpeg = ffmpegRef.current;

      setIsProcessing(true);
      setProgress(0);
      setError(null);

      // Step 1: Fetch intro and outro video blobs from public directory
      const introResponse = await fetch('Intro.mp4');
      const outroResponse = await fetch('Outro.mp4');

      if (!introResponse.ok || !outroResponse.ok) {
        throw new Error("Failed to load intro or outro videos");
      }

      const introBlob = await introResponse.blob();
      const outroBlob = await outroResponse.blob();

      // Step 2: Convert blobs to files
      const introFile = new File([introBlob], "intro.mp4", { type: "video/mp4" });
      const outroFile = new File([outroBlob], "outro.mp4", { type: "video/mp4" });

        // Step 3: Write files to FFmpeg's virtual file system
        await ffmpeg.writeFile('intro.mp4', await fetchFile(introFile));
      setProgress(15);

      await ffmpeg.writeFile('main.mp4', await fetchFile(selectedFile));
      setProgress(30);

      await ffmpeg.writeFile('outro.mp4', await fetchFile(outroFile));
      setProgress(45);


      // Step 4: Convert MP4s to MPEG-TS format (.ts) — fast, no re-encoding
      const a = await ffmpeg.exec([
        '-i', 'intro.mp4',
        '-c', 'copy',
        '-bsf:v', 'h264_mp4toannexb',
        '-f', 'mpegts',
        'intro.ts'
      ]);
      await ffmpeg.readFile('intro.ts');

      const b = await ffmpeg.exec([
        '-i', 'main.mp4',
        '-c', 'copy',
        '-bsf:v', 'h264_mp4toannexb',
        '-f', 'mpegts',
        'main.ts'
      ]);
      await ffmpeg.readFile('main.ts');

      const c = await ffmpeg.exec([
        '-i', 'outro.mp4',
        '-c', 'copy',
        '-bsf:v', 'h264_mp4toannexb',
        '-f', 'mpegts',
        'outro.ts'
      ]);

      await ffmpeg.readFile('outro.ts');
      setProgress(65);

      // Step 5: Concatenate .ts files and remux back to .mp4
      const d = await ffmpeg.exec([
        '-i', 'concat:intro.ts|main.ts|outro.ts',
        '-c', 'copy',
        '-bsf:a', 'aac_adtstoasc',
        'output.mp4'
      ]);

      setProgress(85);

      // Step 6: Read the final output
      const data: any = await ffmpeg.readFile('output.mp4');

      const blob = new Blob([new Uint8Array(data.buffer)], { type: 'video/mp4' });

      const url = URL.createObjectURL(blob);

      setOutputUrl(url);
      setProgress(100);

      // Step 7: Clean up (optional but recommended)
      await ffmpeg.deleteFile('intro.mp4');
      await ffmpeg.deleteFile('main.mp4');
      await ffmpeg.deleteFile('outro.mp4');
      await ffmpeg.deleteFile('intro.ts');
      await ffmpeg.deleteFile('main.ts');
      await ffmpeg.deleteFile('outro.ts');
      await ffmpeg.deleteFile('output.mp4');

    } catch (err) {
      console.error("Error processing video:", err);
      const error = err as Error;
      setError(`An error occurred while processing the video: ${error.message || 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4`}>
      <main className="w-full max-w-md p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Video Splicer
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center mt-2">
            Select a video to splice with intro and outro videos
          </p>
        </div>

        <div className="space-y-4">
          <div
            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={handleButtonClick}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="video/*"
              className="hidden"
              disabled={isProcessing}
            />
            <svg
              className="w-10 h-10 text-gray-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              ></path>
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Click to select a video file or drag and drop
            </p>
          </div>

          {selectedFile && (
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Selected file:
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>

              <button
                onClick={processVideo}
                disabled={isProcessing || !isFFmpegLoaded}
                className={`mt-3 w-full py-2 px-4 rounded-md text-white font-medium text-sm
                  ${isProcessing || !isFFmpegLoaded
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {isProcessing ? 'Processing...' : 'Splice Videos'}
              </button>

              {isProcessing && (
                <div className="mt-3">
                  <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-300 ease-in-out"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                    {progress}% complete
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-200">
                {error}
              </p>
            </div>
          )}

          {outputUrl && (
            <div className="mt-4 p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                Video processing complete!
              </p>
              <a
                href={outputUrl}
                download={`spliced_${selectedFile?.name || 'video'}`}
                className="block w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-md text-center font-medium text-sm"
              >
                Download Spliced Video
              </a>
            </div>
          )}

          {!isFFmpegLoaded && (
            <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                Loading video processing library...
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
